import React, { useState, useRef, useEffect, memo } from 'react';
import Cropper from 'react-easy-crop';
import html2canvas from 'html2canvas';
import { Helmet } from 'react-helmet';
import { getDoc, doc } from 'firebase/firestore';
import { textdb } from '../../firebaseConfig'; // Adjust this path if needed
import { FaDownload, FaWhatsapp, FaFileImage, FaCheckCircle, FaRedo, FaEdit, FaCut, FaShareAlt } from 'react-icons/fa';
import LazyLoad from 'react-lazy-load';
import { useParams } from 'react-router-dom';
import styles from "./Campaign.module.css"; // Using a new CSS Module

// ====================================================================
// FinalRender Component: The heart of the pixel-perfect fix.
// This component's structure perfectly mimics the preview panel in Edit.js.
// ====================================================================
const FinalRender = memo(({ finalImageRef, template, layout, croppedImage, name }) => {
    if (!template || !layout) return null;

    return (
        // This container is hidden off-screen, purely for html2canvas to render.
        <div className={styles.hiddenRenderContainer} aria-hidden="true">
            <div className={styles.editorPreviewWrapper}>
                <div 
                    ref={finalImageRef} 
                    className={styles.mainImagePreview} 
                    style={{ height: `${template.imageHeight}px`, width: `${template.imageWidth}px` }}
                >
                    {/* 1. The Background Image */}
                    <img className={styles.bgImage} src={template.imageUrl} alt="" crossOrigin="anonymous" />
                    
                    {/* 2. The Container for positioned elements, matching Edit.js */}
                    <div className={styles.dragContainer}>
                        
                        {/* --- User Image Placement (Replicates the Draggable Image) --- */}
                        <div style={{
                            position: 'absolute',
                            // Use the exact transform saved by the Draggable component
                            transform: `translate(${layout.imagePosition.x}px, ${layout.imagePosition.y}px)`,
                            // Use the exact dimensions from the placeholder
                            width: `${layout.imageSize.width}px`,
                            height: `${layout.imageSize.height}px`,
                            borderRadius: template.borderRadius,
                            overflow: 'hidden', // Ensures the image respects the border-radius
                        }}>
                            {croppedImage && (
                                <img 
                                    src={croppedImage} 
                                    alt="User" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            )}
                        </div>

                        {/* --- User Text Placement (Replicates the Draggable Text) --- */}
                        <div style={{
                            position: 'absolute',
                            // Use the exact transform saved by the Draggable component
                            transform: `translate(${layout.textPosition.x}px, ${layout.textPosition.y}px)`,
                            
                            // Apply all font and color styles from the placeholder
                            fontFamily: template.fontFamily,
                            color: template.fontColor,
                            fontSize: `${layout.textSize}px`,
                            fontWeight: template.fontWeight,
                            
                            // CRUCIAL: Replicate the placeholder's internal padding and box model
                            padding: '0px 10px',
                            boxSizing: 'border-box',
                            
                            // Other styles for consistency
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});


export const Campaign = () => {
  const { id } = useParams();

  // State for user interaction
  const [name, setName] = useState('');
  const [uploadedImage, setUploadedImage] = useState('');
  const [croppedImage, setCroppedImage] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [canvasImage, setCanvasImage] = useState(null);

  // State for data fetched from Firebase
  const [template, setTemplate] = useState(null);
  const [layout, setLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const finalImageRef = useRef(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const galleryDocRef = doc(textdb, 'galleries', id);
        const positionsDocRef = doc(textdb, 'positions', id);

        const [gallerySnap, positionsSnap] = await Promise.all([
            getDoc(galleryDocRef),
            getDoc(positionsDocRef)
        ]);

        if (gallerySnap.exists()) {
          setTemplate(gallerySnap.data());
        } else {
          console.error("Template not found!");
        }

        const defaultLayout = {
            imagePosition: { x: 50, y: 150 },
            textPosition: { x: 50, y: 50 },
            imageSize: { width: 100, height: 100 },
            textSize: 24,
        };

        if (positionsSnap.exists()) {
          const data = positionsSnap.data();
          setLayout({
            imagePosition: data.imagePosition || defaultLayout.imagePosition,
            textPosition: data.textPosition || defaultLayout.textPosition,
            imageSize: data.imageSize || defaultLayout.imageSize,
            textSize: data.textSize || defaultLayout.textSize,
          });
        } else {
          console.error("Layout data not found! Using default layout.");
          setLayout(defaultLayout);
        }
      } catch (error) {
          console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
    window.scrollTo(0, 0);
  }, [id]);

  // --- Event Handlers (Unchanged) ---
  const handleNameChange = (event) => setName(event.target.value);
  const handleCreateClick = () => name.trim() !== '' && setActiveTab(2);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage(URL.createObjectURL(file));
      setActiveTab(3);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  
  const getCroppedImg = async (imageSrc, crop) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; 
    image.src = imageSrc;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
    return new Promise(resolve => canvas.toBlob(blob => resolve(URL.createObjectURL(blob)), 'image/png'));
  };

  const handleCropSubmit = async () => {
    if (!croppedAreaPixels) return;
    const croppedImg = await getCroppedImg(uploadedImage, croppedAreaPixels);
    setCroppedImage(croppedImg);
    setActiveTab(4); 
  };
  
  useEffect(() => {
    if (activeTab === 4 && croppedImage) {
      setTimeout(() => {
        if (finalImageRef.current) {
          html2canvas(finalImageRef.current, { 
              useCORS: true, 
              backgroundColor: null,
              scale: 2, 
              logging: false,
          }).then(canvas => {
            setCanvasImage(canvas.toDataURL('image/png', 1.0));
          });
        }
      }, 500);
    }
  }, [activeTab, croppedImage]);

  // --- FINAL ACTION HANDLERS (Unchanged) ---
  const downloadImage = () => { /* ... (Same as Imageshare) */ };
  const handleGenericShare = async () => { /* ... (Same as Imageshare) */ };
  const handleWhatsAppShare = () => { /* ... (Same as Imageshare) */ };


  if (isLoading || !template || !layout) {
    return (
        <div className={styles.pageContainer}>
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <h3>Loading Campaign...</h3>
            </div>
        </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Helmet>
        <title>{template.Title1 || 'Create your Poster'}</title>
        {/* other meta tags */}
      </Helmet>
      
      <div className={styles.shareCard}>
        {/* The entire user-facing UI is the same as Imageshare */}
        {/* Step 1 */}
        <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <LazyLoad><img className={styles.cardHeaderImage} style={{height:`${template.imageHeight}px`}} src={template.imageUrl} alt={template.Title1} /></LazyLoad>
            <div className={styles.cardBody}>
                <h3><FaEdit /> Create your Personalised Poster</h3>
                <h4>{template.Title1}</h4>
                <input className={styles.nameInput} placeholder='Enter Your Name' type="text" value={name} onChange={handleNameChange} maxLength="14" />
                <button className={`${styles.actionButton} ${styles.primary}`} onClick={handleCreateClick} disabled={!name.trim()}>Create Poster</button>
            </div>
        </div>
        {/* Step 2 */}
        <div style={{ display: activeTab === 2 ? 'block' : 'none' }}>
            <div className={styles.cardBody}>
                <h3>Welcome, {name}!</h3>
                <h4>Upload your profile photo</h4>
                <label htmlFor="file-input" className={`${styles.actionButton} ${styles.secondary}`}><FaFileImage /> Choose Image</label>
                <input id="file-input" type="file" className={styles.fileInput} accept="image/*" onChange={handleImageUpload} />
            </div>
        </div>
        {/* Step 3 */}
        <div style={{ display: activeTab === 3 ? 'block' : 'none' }}>
             <div className={styles.cardBody}>
                <h3><FaCut /> Crop Your Image</h3>
                <h4>Drag and zoom to get the perfect fit.</h4>
                <div className={styles.cropperContainer}>
                    <Cropper image={uploadedImage} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} cropShape={template.borderRadius === '50%' ? "round" : "rect"} showGrid={false} />
                </div>
                <div className={styles.zoomControlGroup}>
                    <label>Zoom</label>
                    <input type="range" className={styles.zoomSlider} value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(e.target.value)} />
                </div>
                <button className={`${styles.actionButton} ${styles.primary}`} style={{marginTop: '20px'}} onClick={handleCropSubmit}>Submit Crop</button>
            </div>
        </div>
        {/* Step 4 */}
        <div style={{ display: activeTab === 4 ? 'block' : 'none' }}>
            <div className={styles.cardBody}>
                 {canvasImage ? (
                    <>
                        <h3><FaCheckCircle style={{color: '#198754'}} /> Poster Ready!</h3>
                        <h4>Your user flow is correct: Download first, then share!</h4>
                        <img className={styles.finalImagePreview} src={canvasImage} alt="Generated Poster" />
                        <div className={styles.finalActionsContainer}>
                            <button className={`${styles.actionButton} ${styles.primary}`} onClick={downloadImage}><FaDownload/> 1. Download Image</button>
                            <button className={`${styles.actionButton} ${styles.whatsapp}`} onClick={handleWhatsAppShare}><FaWhatsapp/> 2. Share on WhatsApp</button>
                            <button className={`${styles.actionButton} ${styles.subtle}`} onClick={handleGenericShare}><FaShareAlt/> More Share Options</button>
                            <button className={`${styles.actionButton} ${styles.tertiary}`} onClick={() => window.location.reload()}><FaRedo/> Create Another</button>
                        </div>
                    </>
                 ) : (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingSpinner}></div>
                      <h3>Generating your high-quality poster...</h3>
                    </div>
                 )}
            </div>
        </div>
      </div>

      {/* Use the new, corrected FinalRender component */}
      <FinalRender
        finalImageRef={finalImageRef}
        template={template}
        layout={layout}
        croppedImage={croppedImage}
        name={name}
      />
    </div>
  );
};

export default Campaign;